/**
 * CAT Create Page
 *
 * Page for creating a new CAT record.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageShell } from '../../components';
import { CatForm } from './components';
import {
  CatFormData,
  CatReportType,
  AccidentType,
  CatIssuer,
  AccidentLocationType,
  AffectedBodyPart,
  Laterality,
  InjuryNature,
  CreateCatRequest,
  getDefaultCatFormData
} from '../../types/cat';
import { catService } from '../../services/catService';
import { companyService } from '../../services/companyService';
import { employeeService } from '../../services/employeeService';
import { cidCodeService } from '../../services/cidCodeService';

const CatCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState<CatFormData>(getDefaultCatFormData());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // External data for selects
  const [companies, setCompanies] = useState<Array<{ id: number; tradeName: string; legalName: string }>>([]);
  const [employees, setEmployees] = useState<Array<{ id: number; name: string; cpf: string }>>([]);
  const [cidCodes, setCidCodes] = useState<Array<{ id: number; code: string; description: string }>>([]);
  const [companyAddress, setCompanyAddress] = useState<{
    address?: string;
    addressNumber?: string;
    neighborhood?: string;
    zipCode?: string;
    city?: string;
    state?: string;
    ibgeCode?: string;
  }>();

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load companies
      const companiesResponse = await companyService.getCompanies({ limit: 1000 });
      const mappedCompanies = (companiesResponse.data ?? []).map(c => ({
        id: Number(c.id),
        tradeName: c.tradeName || c.legalName,
        legalName: c.legalName
      }));
      setCompanies(mappedCompanies);

      // Load CID codes
      const cidResponse = await cidCodeService.getCidCodes({ limit: 1000 });
      setCidCodes((cidResponse.cidCodes ?? []).map(c => ({
        id: Number(c.id),
        code: c.code,
        description: c.description
      })));
    } catch {
      enqueueSnackbar('Erro ao carregar dados iniciais', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async (companyId: number) => {
    try {
      const data = await employeeService.getEmployeesByCompany(companyId);
      setEmployees(data ?? []);
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  const handleFormChange = (data: Partial<CatFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
    // Clear errors for changed fields
    const changedKeys = Object.keys(data);
    setErrors(prev => {
      const newErrors = { ...prev };
      changedKeys.forEach(key => delete newErrors[key]);
      return newErrors;
    });
  };

  const handleCompanyChange = async (companyId: number) => {
    setEmployees([]);
    setFormData(prev => ({ ...prev, employeeId: '' }));
    loadEmployees(companyId);

    try {
      const company = await companyService.getCompanyById(String(companyId));
      setCompanyAddress({
        address: company.address,
        addressNumber: company.addressNumber,
        neighborhood: company.neighborhood,
        zipCode: company.zipCode,
        city: company.city,
        state: company.state,
        ibgeCode: company.ibgeCode,
      });
    } catch {
      setCompanyAddress(undefined);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.companyId) {
      newErrors.companyId = 'Empresa é obrigatória';
    }
    if (!formData.employeeId) {
      newErrors.employeeId = 'Funcionário é obrigatório';
    }
    if (!formData.reportType) {
      newErrors.reportType = 'Tipo de CAT é obrigatório';
    }
    if (!formData.accidentType) {
      newErrors.accidentType = 'Tipo de acidente é obrigatório';
    }
    if (!formData.accidentDate) {
      newErrors.accidentDate = 'Data do acidente é obrigatória';
    }
    if (!formData.generatingSituation) {
      newErrors.generatingSituation = 'Situação geradora é obrigatória';
    }
    if (!formData.issuer) {
      newErrors.issuer = 'Emissor é obrigatório';
    }
    if (!formData.accidentLocationType) {
      newErrors.accidentLocationType = 'Tipo de local é obrigatório';
    }
    if (!formData.codParteAting) {
      newErrors.affectedBodyPart = 'Parte do corpo atingida é obrigatória (Tabela 13)';
    }
    if (!formData.codNatLesao) {
      newErrors.injuryNature = 'Natureza da lesão é obrigatória (Tabela 17)';
    }
    if (!formData.codAgntCausador) {
      newErrors.causingAgent = 'Agente causador é obrigatório (Tabela 14)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      enqueueSnackbar('Preencha todos os campos obrigatórios', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      // Convert form data to API request
      const request: CreateCatRequest = {
        employeeId: formData.employeeId as number,
        companyId: formData.companyId as number,
        reportType: formData.reportType as CatReportType,
        accidentType: formData.accidentType as AccidentType,
        accidentDate: formData.accidentDate,
        accidentTime: formData.accidentTime || undefined,
        hoursWorkedBefore: formData.hoursWorkedBefore || undefined,
        generatingSituation: formData.generatingSituation,
        issuer: formData.issuer as CatIssuer,
        isDeathReport: formData.isDeathReport,
        accidentLocationType: formData.accidentLocationType as AccidentLocationType,
        streetType: formData.streetType || undefined,
        streetName: formData.streetName || undefined,
        streetNumber: formData.streetNumber || undefined,
        complement: formData.complement || undefined,
        neighborhood: formData.neighborhood || undefined,
        zipCode: formData.zipCode || undefined,
        cityCode: formData.cityCode || undefined,
        state: formData.state || undefined,
        countryCode: formData.countryCode || undefined,
        affectedBodyPart: (formData.affectedBodyPart || undefined) as AffectedBodyPart,
        laterality: (formData.laterality || undefined) as Laterality | undefined,
        injuryNature: (formData.injuryNature || undefined) as InjuryNature,
        injuryDescription: formData.injuryDescription || undefined,
        causingAgent: formData.causingAgent || '',
        probableDiagnosis: formData.probableDiagnosis || undefined,
        cidCodeId: formData.cidCodeId ? Number(formData.cidCodeId) : undefined,
        treatmentDate: formData.treatmentDate || undefined,
        treatmentTime: formData.treatmentTime || undefined,
        wasHospitalized: formData.wasHospitalized || undefined,
        treatmentDuration: formData.treatmentDuration ? Number(formData.treatmentDuration) : undefined,
        willBeAbsent: formData.willBeAbsent || undefined,
        issuerName: formData.issuerName || undefined,
        classEntity: formData.classEntity || undefined,
        classEntityNumber: formData.classEntityNumber || undefined,
        classEntityState: formData.classEntityState || undefined,
        sendToEsocial: formData.sendToEsocial,
        esocialEnvironment: formData.esocialEnvironment || undefined,
        notes: formData.notes || undefined,
        codParteAting: formData.codParteAting || undefined,
        codNatLesao: formData.codNatLesao || undefined,
        codAgntCausador: formData.codAgntCausador || undefined,
        codSitGeradora: formData.codSitGeradora || undefined,
      };

      const cat = await catService.create(request);
      enqueueSnackbar('CAT criada com sucesso', { variant: 'success' });
      navigate(`/cats/${cat.id}`);
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao criar CAT', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Início' },
    { label: 'Segurança do Trabalho' },
    { label: 'CAT' },
    { label: 'Nova CAT' },
  ];

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbItems} title="Nova CAT">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbItems}
      title="Nova CAT"
      actions={
        <>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/cats')}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </>
      }
    >
      {/* Form */}
      <Card>
        <CardContent>
          <CatForm
            formData={formData}
            onChange={handleFormChange}
            errors={errors}
            companies={companies}
            employees={employees}
            cidCodes={cidCodes}
            companyAddress={companyAddress}
            onCompanyChange={handleCompanyChange}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default CatCreatePage;
